import type { CSSProperties, ReactNode } from 'react';
import AutoScroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';

import {
  ImagePriorityContext,
  useImagePriority,
} from '@/components/blocks/section-image/image-priority-context';
import {
  Accordion,
  type AccordionButtonVariant,
  AccordionContent,
  type AccordionIconStyle,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavControls,
} from '@/components/ui/carousel';
import { useAccordionValue } from '@/hooks/use-accordion-value';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import { readButtonConfig } from '@/lib/blocks/button-links';
import type {
  CarouselDisplayConfig,
  CarouselItemsPerView,
  CarouselNavButtonStyle,
  CarouselNavPosition,
  CarouselOrientation,
  CarouselVerticalHeight,
  CollectionCardAlign,
  CollectionCardConfig,
  CollectionCardField,
  CollectionCardPadding,
  CollectionGap,
  CollectionItem,
  CollectionLayoutConfig,
} from '@/lib/blocks/collection/blocks';
import {
  getAccordionContainerPaddingClassName,
  getAccordionSingleGapClassName,
  getAlignmentClassName,
  getAlternatingContainerClassName,
  getCardBgClassName,
  getCardContentGapClassName,
  getCarouselContentClassName,
  getCarouselContentPaddingClassName,
  getCarouselItemClassName,
  getCollectionContainerClassName,
  getVerticalCarouselHeightClassName,
  repeatItemsForAutoScroll,
  resolveAccordionFields,
} from '@/lib/blocks/collection/blocks';
import type { CollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { readImageValue, readString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { alternatingImagePosition, CollectionCard } from '../collection/collection-card';
import type { CollectionCardSideBySideMode, RenderField } from '../collection/types';
import { ButtonBlock } from '../primitives/button-block';
import { ImageBlock } from '../primitives/image-block';
import { TextBlock } from '../primitives/text-block';

interface AccordionCollectionRendererProps {
  items: CollectionItem[];
  cardConfig: CollectionCardConfig;
  padding?: CollectionCardPadding;
  containerStyle?: CSSProperties;
  expandFirstItem?: boolean;
  allowMultiple?: boolean;
  showDividers?: boolean;
  iconStyle?: AccordionIconStyle;
  buttonVariant?: AccordionButtonVariant;
  layout?: CollectionLayoutConfig;
  hasCardStyling?: boolean;
  questionSlot?: string;
  renderBodyField?: (item: CollectionItem) => RenderField;
}

export function AccordionCollectionRenderer({
  items,
  cardConfig,
  padding = 'md',
  containerStyle,
  expandFirstItem = false,
  allowMultiple = true,
  showDividers = true,
  iconStyle = 'chevron',
  buttonVariant = 'filled',
  layout,
  hasCardStyling = false,
  questionSlot,
  renderBodyField,
}: AccordionCollectionRendererProps) {
  const cardColors = useEffectiveColors();
  const cardBgClassName = getCardBgClassName(containerStyle, cardColors);
  const { questionField, bodyFields } = resolveAccordionFields(cardConfig.fields, questionSlot);
  const [accordionValue, setAccordionValue] = useAccordionValue({
    expandFirstItem,
    firstItemId: items[0]?.id,
  });

  if (items.length === 0) return null;

  const isMultiColumn = layout && layout.columns.desktop > 1;
  const defaultRenderField = makePublicRenderField(cardConfig.align);

  const renderItem = (item: CollectionItem) => {
    const questionContent = questionField ? item[questionField.slot] : null;
    const fallbackText = questionField ? `${questionField.label}…` : 'Question…';
    const itemRenderField = renderBodyField?.(item) ?? defaultRenderField;
    return (
      <AccordionItem
        key={item.id}
        value={item.id}
        className={!showDividers ? 'border-b-0' : undefined}
      >
        <AccordionTrigger
          iconStyle={iconStyle}
          buttonVariant={buttonVariant}
          className={cn(
            'min-w-0 py-2 text-base leading-snug hover:no-underline',
            getAlignmentClassName(cardConfig.align),
          )}
        >
          <TextBlock content={questionContent ?? fallbackText} className="min-w-0 flex-1" />
        </AccordionTrigger>
        <AccordionContent>
          {bodyFields.length > 0 ? (
            <div className={cn('flex flex-col pb-5', getCardContentGapClassName(cardConfig.gap))}>
              {bodyFields.map((field) => (
                <div key={field.slot} className="empty:hidden">
                  {itemRenderField(field, item[field.slot])}
                </div>
              ))}
            </div>
          ) : (
            <div className="pb-5">
              <TextBlock content="Answer…" />
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  if (isMultiColumn) {
    const gridClassName = cn(getCollectionContainerClassName(layout), 'items-start');
    const itemWrapperClassName = cn(
      'w-full rounded-md border',
      getAccordionContainerPaddingClassName(padding),
      getAlignmentClassName(cardConfig.align),
      cardBgClassName,
    );
    const renderGridItems = items.map((item) => (
      <div key={item.id} className={itemWrapperClassName} style={containerStyle}>
        {renderItem(item)}
      </div>
    ));

    return allowMultiple ? (
      <Accordion
        type="multiple"
        value={accordionValue}
        onValueChange={setAccordionValue}
        className={gridClassName}
      >
        {renderGridItems}
      </Accordion>
    ) : (
      <Accordion
        type="single"
        collapsible
        value={accordionValue[0] ?? ''}
        onValueChange={(v) => setAccordionValue(v ? [v] : [])}
        className={gridClassName}
      >
        {renderGridItems}
      </Accordion>
    );
  }

  const sharedClassName = cn(
    'w-full',
    getAccordionSingleGapClassName(layout?.gap ?? 'sm'),
    getAlignmentClassName(cardConfig.align),
  );

  const renderSingleColItem = (item: CollectionItem) => {
    const itemNode = renderItem(item);
    if (!hasCardStyling) return itemNode;
    return (
      <div
        key={item.id}
        className={cn(
          'rounded-lg',
          getAccordionContainerPaddingClassName(padding),
          cardBgClassName,
        )}
        style={containerStyle}
      >
        {itemNode}
      </div>
    );
  };

  return allowMultiple ? (
    <Accordion
      type="multiple"
      value={accordionValue}
      onValueChange={setAccordionValue}
      className={sharedClassName}
    >
      {items.map(renderSingleColItem)}
    </Accordion>
  ) : (
    <Accordion
      type="single"
      collapsible
      value={accordionValue[0] ?? ''}
      onValueChange={(v) => setAccordionValue(v ? [v] : [])}
      className={sharedClassName}
    >
      {items.map(renderSingleColItem)}
    </Accordion>
  );
}

const BUTTON_ALIGN_CLASSNAMES: Record<CollectionCardAlign, string> = {
  start: 'flex w-full justify-start',
  center: 'flex w-full justify-center',
  end: 'flex w-full justify-end',
};

function makePublicRenderField(align: CollectionCardAlign): RenderField {
  return (field, value) => (
    <CollectionCardFieldRenderer field={field} value={value} align={align} />
  );
}

interface CollectionCardFieldProps {
  field: CollectionCardField;
  value: unknown;
  align?: CollectionCardAlign;
}

function textValueForField(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return readString((value as Record<string, unknown>).name) ?? value;
  }

  if (!Array.isArray(value)) {
    return value;
  }

  const labels = value
    .map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return readString((item as Record<string, unknown>).name);
      }

      return readString(item);
    })
    .filter(Boolean);

  return labels.join(' • ');
}

export function CollectionCardFieldRenderer({
  field,
  value,
  align = 'start',
}: CollectionCardFieldProps) {
  const isPageEditor = useIsPageEditor();
  const isPreview = useIsPreviewMode();

  switch (field.kind) {
    case 'text':
      return <TextBlock content={textValueForField(value)} />;
    case 'image': {
      const itemRecord =
        value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      const mergedBlockProps = { ...field.blockProps };
      if (itemRecord.fit !== undefined) mergedBlockProps.fit = itemRecord.fit;
      if (itemRecord.shape !== undefined) mergedBlockProps.shape = itemRecord.shape;
      if (itemRecord.focusX !== undefined) mergedBlockProps.focusX = itemRecord.focusX;
      if (itemRecord.focusY !== undefined) mergedBlockProps.focusY = itemRecord.focusY;
      return <ImageBlock image={readImageValue(value)} blockProps={mergedBlockProps} />;
    }
    case 'button': {
      const buttonProps =
        value && typeof value === 'object' && !Array.isArray(value)
          ? readButtonConfig(value as Record<string, unknown>)
          : readButtonConfig({});
      const isPublicView = !isPageEditor || isPreview;
      const hasLink = Boolean(
        buttonProps.link || buttonProps.internalPageId || buttonProps.resolvedLink,
      );
      if (isPublicView && !hasLink) return null;
      return (
        <div className={BUTTON_ALIGN_CLASSNAMES[align]}>
          <ButtonBlock {...buttonProps} />
        </div>
      );
    }
    default:
      return null;
  }
}

interface CollectionCardRendererProps {
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
  mediaLayout?: CollectionMediaLayout;
  sideBySideMode?: CollectionCardSideBySideMode;
  paddingClassName?: string;
  contentPaddingClassName?: string;
  renderField?: RenderField;
}

export function CollectionCardRenderer({
  item,
  cardConfig,
  mediaLayout,
  sideBySideMode,
  paddingClassName,
  contentPaddingClassName,
  renderField,
}: CollectionCardRendererProps) {
  return (
    <CollectionCard
      item={item}
      cardConfig={cardConfig}
      mediaLayout={mediaLayout}
      sideBySideMode={sideBySideMode}
      paddingClassName={paddingClassName}
      contentPaddingClassName={contentPaddingClassName}
      renderField={renderField ?? makePublicRenderField(cardConfig.align)}
    />
  );
}

interface AlternatingCollectionCardRendererProps {
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
  alternatingRowIndex: number;
  mediaLayout?: CollectionMediaLayout;
  paddingClassName?: string;
  renderField?: RenderField;
  cardStyle?: CSSProperties;
  hasCardStyling?: boolean;
}

export function AlternatingCollectionCardRenderer({
  item,
  cardConfig,
  alternatingRowIndex,
  mediaLayout,
  paddingClassName,
  renderField,
  cardStyle,
  hasCardStyling = false,
}: AlternatingCollectionCardRendererProps) {
  const cardColors = useEffectiveColors();
  const card = (
    <CollectionCard
      item={item}
      cardConfig={cardConfig}
      mediaLayout={
        mediaLayout
          ? { ...mediaLayout, position: alternatingImagePosition(alternatingRowIndex) }
          : undefined
      }
      sideBySideMode="alternating"
      paddingClassName={paddingClassName}
      renderField={renderField ?? makePublicRenderField(cardConfig.align)}
    />
  );

  if (hasCardStyling || cardStyle) {
    return (
      <div
        className={cn(
          'min-w-0',
          hasCardStyling && 'rounded-lg',
          getCardBgClassName(cardStyle, cardColors),
        )}
        style={cardStyle}
      >
        {card}
      </div>
    );
  }

  return card;
}

interface AlternatingRendererProps {
  items: CollectionItem[];
  cardConfig: CollectionCardConfig;
  alternatingGap: CollectionGap;
  paddingClassName: string;
  cardStyle?: CSSProperties;
  hasCardStyling?: boolean;
  renderField?: RenderField;
  mediaLayout?: CollectionMediaLayout;
  renderCard?: (item: CollectionItem, index: number) => ReactNode;
}

export function AlternatingRenderer({
  items,
  cardConfig,
  alternatingGap,
  paddingClassName,
  cardStyle,
  hasCardStyling = false,
  renderField,
  mediaLayout,
  renderCard,
}: AlternatingRendererProps) {
  const cardColors = useEffectiveColors();
  const cardBgClassName = getCardBgClassName(cardStyle, cardColors);
  return (
    <div className={getAlternatingContainerClassName(alternatingGap)}>
      {items.map((item, index) =>
        renderCard ? (
          hasCardStyling || cardStyle ? (
            <div
              key={item.id}
              className={cn('min-w-0', hasCardStyling && 'rounded-lg', cardBgClassName)}
              style={cardStyle}
            >
              {renderCard(item, index)}
            </div>
          ) : (
            <div key={item.id}>{renderCard(item, index)}</div>
          )
        ) : (
          <AlternatingCollectionCardRenderer
            key={item.id}
            item={item}
            cardConfig={cardConfig}
            alternatingRowIndex={index}
            mediaLayout={mediaLayout}
            paddingClassName={paddingClassName}
            renderField={renderField}
            cardStyle={cardStyle}
            hasCardStyling={hasCardStyling}
          />
        ),
      )}
    </div>
  );
}

interface CollectionGridRendererProps {
  items: CollectionItem[];
  cardConfig: CollectionCardConfig;
  layout: CollectionLayoutConfig;
  mediaLayout?: CollectionMediaLayout;
  paddingClassName: string;
  cardStyle?: CSSProperties;
  hasCardStyling?: boolean;
  renderField?: RenderField;
  renderCard?: (item: CollectionItem) => ReactNode;
}

export function CollectionGridRenderer({
  items,
  cardConfig,
  layout,
  mediaLayout,
  paddingClassName,
  cardStyle,
  hasCardStyling = false,
  renderField,
  renderCard,
}: CollectionGridRendererProps) {
  const cardColors = useEffectiveColors();
  const cardBgClassName = getCardBgClassName(cardStyle, cardColors);
  return (
    <div className={getCollectionContainerClassName(layout)}>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn('min-w-0 h-full', hasCardStyling && 'rounded-lg', cardBgClassName)}
          style={cardStyle}
        >
          <div className={cn('h-full', paddingClassName)}>
            {renderCard ? (
              renderCard(item)
            ) : (
              <CollectionCardRenderer
                item={item}
                cardConfig={cardConfig}
                mediaLayout={mediaLayout}
                renderField={renderField}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

interface CollectionCarouselRendererProps {
  items: CollectionItem[];
  display: {
    itemsPerView: { mobile: number; tablet: number; desktop: number };
    gap: CollectionGap;
    arrows: boolean;
    autoScroll: boolean;
    orientation: CarouselOrientation;
    verticalHeight: CarouselVerticalHeight;
    navPosition?: CarouselNavPosition;
    navButtonStyle?: CarouselNavButtonStyle;
  };
  cardConfig: CollectionCardConfig;
  mediaLayout?: CollectionMediaLayout;
  paddingClassName: string;
  cardStyle?: CSSProperties;
  hasCardStyling?: boolean;
  renderField?: RenderField;
  renderFieldForItem?: (item: CollectionItem) => RenderField | undefined;
}

export function CollectionCarouselRenderer({
  items,
  display,
  cardConfig,
  mediaLayout,
  paddingClassName,
  cardStyle,
  hasCardStyling = false,
  renderField,
  renderFieldForItem,
}: CollectionCarouselRendererProps) {
  const cardColors = useEffectiveColors();
  const isPriority = useImagePriority();
  const cardBgClassName = getCardBgClassName(cardStyle, cardColors);
  const carouselDisplay: CarouselDisplayConfig = {
    itemsPerView: {
      mobile: display.itemsPerView.mobile as CarouselItemsPerView,
      tablet: display.itemsPerView.tablet as CarouselItemsPerView,
      desktop: display.itemsPerView.desktop as CarouselItemsPerView,
    },
    gap: display.gap,
    arrows: display.arrows,
    orientation: display.orientation,
    navPosition: display.navPosition ?? 'sides',
    navButtonStyle: display.navButtonStyle ?? 'outline',
  };
  const isVertical = display.orientation === 'vertical';
  const useAutoScroll = isVertical || display.autoScroll;
  const displayItems = useAutoScroll
    ? repeatItemsForAutoScroll(items, carouselDisplay.itemsPerView.desktop, isVertical ? 5 : 2)
    : items;
  const plugins = useAutoScroll
    ? [
        AutoScroll({
          speed: 0.7,
          startDelay: 0,
          stopOnMouseEnter: false,
          stopOnInteraction: false,
          stopOnFocusIn: false,
        }),
      ]
    : !carouselDisplay.arrows
      ? [Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })]
      : [];

  return (
    <div
      className={
        isVertical ? getVerticalCarouselHeightClassName(display.verticalHeight) : undefined
      }
    >
      <Carousel
        orientation={display.orientation}
        className={cn(
          isVertical ? 'h-full w-full' : 'w-full',
          !isVertical && carouselDisplay.navPosition === 'top-right' && 'pt-10',
        )}
        opts={{
          align: 'start',
          containScroll: useAutoScroll ? false : 'trimSnaps',
          loop:
            useAutoScroll ||
            !carouselDisplay.arrows ||
            items.length > carouselDisplay.itemsPerView.desktop,
          dragFree: useAutoScroll,
        }}
        plugins={plugins}
      >
        <CarouselContent
          className={getCarouselContentClassName(carouselDisplay.gap, display.orientation)}
        >
          {displayItems.map((item, index) => (
            <CarouselItem
              key={`${item.id}-${index}`}
              className={getCarouselItemClassName(carouselDisplay)}
            >
              <ImagePriorityContext
                value={isPriority && index < carouselDisplay.itemsPerView.desktop}
              >
                <div
                  className={cn(
                    'h-full',
                    paddingClassName,
                    hasCardStyling && 'rounded-lg',
                    cardBgClassName,
                  )}
                  style={cardStyle}
                >
                  <CollectionCardRenderer
                    item={item}
                    cardConfig={cardConfig}
                    mediaLayout={mediaLayout}
                    sideBySideMode="carousel"
                    contentPaddingClassName={getCarouselContentPaddingClassName({
                      ...carouselDisplay,
                      autoScroll: display.autoScroll,
                    })}
                    renderField={renderFieldForItem?.(item) ?? renderField}
                  />
                </div>
              </ImagePriorityContext>
            </CarouselItem>
          ))}
        </CarouselContent>
        {!useAutoScroll && carouselDisplay.arrows ? (
          <CarouselNavControls
            navPosition={carouselDisplay.navPosition}
            navButtonStyle={carouselDisplay.navButtonStyle}
            className={paddingClassName?.replace(/^p-/, 'pl-')}
          />
        ) : null}
      </Carousel>
    </div>
  );
}
